import {
  LayoutTypes,
  Question,
  QuestionGroup,
} from "@stormwater-software/lib-inspection-templates";
import React, { PropsWithChildren, useContext, useMemo } from "react";
import { useQueryClient } from "react-query";
import { pdfLayoutKey } from "../hooks/pdfLayout";
import { PdfLayoutGroup } from "../utils/api/pdfLayout";
import TemplateEditorContext from "./TemplateEditorContext";

export type EditorQuestionGroup = QuestionGroup & {
  order: number;
  questions: Array<Question & { questionWidth: string }>;
};

export type IQuestionGroupProps = {
  questionGroup: EditorQuestionGroup;
  refetchQuestionGroup: () => Promise<unknown>;
};

const throwError = () => {
  throw new Error("QuestionGroupProvider not loaded");
};

const Context = React.createContext<IQuestionGroupProps>({
  questionGroup: { layout_type: LayoutTypes["FORM"], order: 0, questions: [] },
  refetchQuestionGroup: throwError,
});

// Adapt the pdf-layout payload to the QuestionGroup shape the editor forms use.
export const toEditorQuestionGroup = (
  group: PdfLayoutGroup,
): EditorQuestionGroup =>
  ({
    id: group.id,
    name: group.name,
    description: group.description ?? "",
    order: group.order,
    layout_type: group.layout_type as LayoutTypes,
    table_headers: group.table_headers,
    questions: group.questions.map(q => ({
      id: q.id,
      groupId: group.id,
      input_label: q.label,
      sort_order: q.sort_order,
      question_type_id: q.question_type_id,
      features: q.features,
      questionWidth: String(q.width),
    })),
  }) as EditorQuestionGroup;

export const QuestionGroupProvider: React.FC<
  PropsWithChildren<{ group: PdfLayoutGroup }>
> = ({ group, children }) => {
  const { templateId } = useContext(TemplateEditorContext);
  const queryClient = useQueryClient();

  const questionGroup = useMemo(() => toEditorQuestionGroup(group), [group]);

  const refetchQuestionGroup = () =>
    queryClient.invalidateQueries(pdfLayoutKey(templateId));

  return (
    <Context.Provider value={{ questionGroup, refetchQuestionGroup }}>
      {children}
    </Context.Provider>
  );
};

export default Context;
